import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Paper,
    alpha,
    useTheme
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    CloudUpload as UploadIcon,
    Campaign as AnnouncementIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
    Image as ImageIcon
} from '@mui/icons-material';
import {
    getAnnouncements,
    createAnnouncement,
    toggleAnnouncement,
    deleteAnnouncement
} from '../../services/announcement';

const Announcement = () => {
    const theme = useTheme();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Modal state
    const [openModal, setOpenModal] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [form, setForm] = useState({
        title: '',
        type: 'popup',
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);

    const fetchAnnouncements = async () => {
        setLoading(true);
        try {
            const response = await getAnnouncements();
            if (response.status) {
                setAnnouncements(response.data || []);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const handleToggle = async (id, currentStatus) => {
        try {
            const response = await toggleAnnouncement(id, !currentStatus);
            if (response.status) {
                setAnnouncements(prev =>
                    prev.map(ann => ann._id === id ? { ...ann, isActive: !currentStatus } : ann)
                );
                setSuccess(`Announcement ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this announcement?')) return;
        try {
            const response = await deleteAnnouncement(id);
            if (response.status) {
                setAnnouncements(prev => prev.filter(ann => ann._id !== id));
                setSuccess('Announcement deleted successfully');
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm({ ...form, image: file });
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        if (!form.title || !form.image) {
            setError('Please provide both title and image');
            return;
        }

        setFormLoading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('title', form.title);
            formData.append('type', form.type);
            formData.append('image', form.image);

            const response = await createAnnouncement(formData);
            if (response.status) {
                setSuccess('Announcement created successfully');
                setOpenModal(false);
                setForm({ title: '', type: 'popup', image: null });
                setImagePreview(null);
                fetchAnnouncements();
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setFormLoading(false);
        }
    };

    return (
        <Box sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 3,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            display: 'flex'
                        }}
                    >
                        <AnnouncementIcon fontSize="large" />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary' }}>
                            Announcements
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage promotional banners and popup notifications for associates
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenModal(true)}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.2,
                        fontWeight: 700,
                        textTransform: 'none',
                        boxShadow: '0 8px 16px ' + alpha(theme.palette.primary.main, 0.2)
                    }}
                >
                    New Announcement
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {announcements.length === 0 ? (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 10, textAlign: 'center', borderRadius: 4, border: '2px dashed', borderColor: 'grey.200', backgroundColor: 'transparent' }}>
                                <ImageIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">No announcements found</Typography>
                                <Typography variant="body2" color="text.disabled">Create your first announcement to reach your associates</Typography>
                            </Paper>
                        </Grid>
                    ) : (
                        announcements.map((ann) => (
                            <Grid item xs={12} sm={6} md={4} key={ann._id}>
                                <Card
                                    sx={{
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                                        transition: 'transform 0.3s, box-shadow 0.3s',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                                        }
                                    }}
                                >
                                    <Box sx={{ position: 'relative', pt: '56.25%', backgroundColor: 'grey.100' }}>
                                        <Box
                                            component="img"
                                            src={ann.imageUrl}
                                            alt={ann.title}
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                display: 'flex',
                                                gap: 1
                                            }}
                                        >
                                            <Tooltip title={ann.isActive ? 'Active' : 'Inactive'}>
                                                <Box
                                                    sx={{
                                                        backgroundColor: ann.isActive ? 'success.main' : 'error.main',
                                                        color: 'white',
                                                        p: 0.5,
                                                        borderRadius: '50%',
                                                        display: 'flex'
                                                    }}
                                                >
                                                    {ann.isActive ? <ActiveIcon fontSize="small" /> : <InactiveIcon fontSize="small" />}
                                                </Box>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                    {ann.title}
                                                </Typography>
                                                <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 800, color: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.1), px: 1, py: 0.5, borderRadius: 1 }}>
                                                    {ann.type}
                                                </Typography>
                                            </Box>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(ann._id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'grey.50' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                Active Status
                                            </Typography>
                                            <Switch
                                                checked={ann.isActive}
                                                onChange={() => handleToggle(ann._id, ann.isActive)}
                                                color="success"
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>
            )}

            {/* Add New Dialog */}
            <Dialog
                open={openModal}
                onClose={() => !formLoading && setOpenModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 4, p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '1.5rem', pb: 1 }}>
                    Create Announcement
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        Fill in the details below to create a new announcement.
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Announcement Title"
                                placeholder="e.g. Summer Festival Offer"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={form.type}
                                    label="Type"
                                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                                    disabled
                                >
                                    <MenuItem value="popup">Popup Modal</MenuItem>
                                    <MenuItem value="banner">Dashboard Banner</MenuItem>
                                    <MenuItem value="sidebar">Sidebar Banner</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    border: '2px dashed',
                                    borderColor: imagePreview ? 'primary.main' : 'grey.300',
                                    borderRadius: 3,
                                    p: 3,
                                    textAlign: 'center',
                                    backgroundColor: imagePreview ? alpha(theme.palette.primary.main, 0.02) : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                    }
                                }}
                                onClick={() => document.getElementById('image-upload').click()}
                            >
                                <input
                                    type="file"
                                    id="image-upload"
                                    hidden
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                {imagePreview ? (
                                    <Box sx={{ position: 'relative' }}>
                                        <Box
                                            component="img"
                                            src={imagePreview}
                                            sx={{ maxHeight: 200, maxWidth: '100%', borderRadius: 2 }}
                                        />
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>
                                                Click to change image
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <>
                                        <UploadIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            Upload Image
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Recommended size: 1200x600px
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button
                        onClick={() => setOpenModal(false)}
                        sx={{ fontWeight: 700, color: 'text.secondary' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={formLoading}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            fontWeight: 700,
                            boxShadow: 'none'
                        }}
                    >
                        {formLoading ? <CircularProgress size={24} color="inherit" /> : 'Publish Now'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Announcement;